import * as $bootstrapHelper from '../../core/helpers/bootstrap.helper';
import * as $cartHelper from '../../core/helpers/cart.helper';
import { getEventTrackingCodesByAction, intialiseTracking, waitForFbq, waitForTtq } from '../../core/helpers/tracking-scripts.helper';
import { CartModel, TicketModel } from '../../core/models/cart.model';
import { CheckoutConfig, getCheckoutConfig } from '../../core/models/checkout-config.model';
import { CheckoutPayload } from '../../core/models/checkout-payload.model';
import { EnvModel, getEnvModel } from '../../core/models/env.model';
import { TermsAndConditionsModel } from '../../core/models/terms-and-conditions.model';
import { UiStringModel } from '../../core/models/ui-string.model';
import { UserBookingDetailsModel } from '../../core/models/user-booking-details.model';
import DataService from '../../core/services/data.service';
import UrlService from '../../core/services/url.service';

declare global {
    var payPalConfirmationViewModel: PayPalConfirmationViewModel;
}

export class PayPalConfirmationViewModel {
    private _env: EnvModel;
    private checkout: CheckoutConfig;

    public dataService: DataService;

    public uiStrings: KnockoutObservable<UiStringModel>;
    public urlService: KnockoutObservable<UrlService>;
    public bookingSitePath: KnockoutObservable<string>;
    public currency: KnockoutObservable<string>;
    public shouldShowAcknowledgement: KnockoutObservable<boolean>;

    public links = ko.observable({});

    public isCartLoaded = ko.observable(false);
    public isLoading = ko.observable(false);

    public showSpinner = ko.pureComputed(() => !this.ready() || this.isLoading());

    public ready = ko.pureComputed(() => this.isCartLoaded());

    public permissionToContact = ko.observable(false);
    public showReceiveUpdatesTickbox = ko.observable(false);
    public acknowledgedTerms = ko.observable(true);

    public showProcessingFeeDetails = ko.observable(false);
    public shoppingCartVm = ko.observable<CartModel>(null);
    public shouldShowPromoMessage = ko.observable(false);
    public eventTerms = ko.observableArray<TermsAndConditionsModel>([]);

    public payerFirstName = ko.observable();
    public payerLastName = ko.observable();
    public payerEmail = ko.observable();
    public bookingEmail = ko.observable();
    public payerAddressLine1 = ko.observable();
    public payerAddressLine2 = ko.observable();
    public payerAddressPostalCode = ko.observable();
    public payerAddressCountryCode = ko.observable();
    public payerPhoneNumber = ko.observable();
    public readonly accountName = ko.pureComputed(() => this.shoppingCartVm()?.accountName);

    public paypalOrderId = ko.observable();
    public paypalNonce = ko.observable();
    public trackingTag = ko.observable('');
    public customId = ko.observable('');
    public isWhiteLabel = ko.observable(false);
    public isWidget = ko.observable(false);
    public widgetType = ko.observable(null);
    public widgetEventId = ko.observable(0);
    public isEventListingWidget = ko.observable(false);
    public widgetAccountId = ko.observable(0);
    public paymentGatewayId = ko.observable(0);

    public hasTermsError = ko.observable(false);
    public termsErrorMessage = ko.observable('&nbsp;');

    // TRACKING
    public EVENT_TRACKET_PREFIX = 'eoTracker';
    public shouldFBTrackSubmitPayment = ko.observable(false);
    private shouldTikTokTrackSubmitPayment = ko.observable(false);
    public shouldGATrackSubmitPayment = ko.observable(false);
    public googleTrackingCodes = ko.observableArray([]);
    //error messages
    public showErrorMessage = ko.observable(false);
    public errorMessage = ko.observable('');


    public enableEventTermsLink = ko.pureComputed(() =>
        this.shoppingCartVm()
        && this.shoppingCartVm().tickets
        && this.shoppingCartVm().tickets.length > 0);

    public constructor() {
        this._env = getEnvModel();
        this.checkout = getCheckoutConfig()!;


        
        this.uiStrings = ko.observable((window as any).global.uiStrings as UiStringModel);
        this.urlService = ko.observable(new UrlService((window as any).env));

        this.dataService = new DataService(this._env); // TO DO: User env model

        this.shouldShowAcknowledgement = ko.observable(this.checkout.isInEU);
        this.bookingSitePath = ko.observable(this._env.bookingSitePath);
        this.currency = ko.observable(this.uiStrings().currency);

        this.showReceiveUpdatesTickbox = ko.observable(this.uiStrings().receiveUpdatesTickboxEnabled);

        if (!this.showReceiveUpdatesTickbox || !this.checkout.permissionToSendPromotionalEmails) {
            this.permissionToContact = ko.observable(null);
        }

        if (this.checkout.isInEU) {
            this.permissionToContact(false);
            this.acknowledgedTerms(false);
        }

        // subscriptions
        this.acknowledgedTerms.subscribe(() => {
            if (this.hasTermsError()) {
                this.hasTermsError(false);
                this.termsErrorMessage('&nbsp;');
            }
        });

        
        this.InitializeFormFields();
        this.InitRecaptcha();

        this.dataService.GetShoppingCart()
            .then((res: any) => this.GetShoppingCartSuccessCallback(res), () => this.GetShoppingCartErrorCallback());

        global.payPalConfirmationViewModel = this;
    }
    
    public showEventTerms() {
        const eventIds: any[] = [];
        $.each(this.shoppingCartVm().tickets, (index: number, value: TicketModel) => {
            const i = eventIds.findIndex((val: number) => val === value.event.id);

            if (i < 0) {
                eventIds.push(value.event.id);
            }
        });

        if (eventIds.length > 0) {
            if (this.eventTerms().length !== eventIds.length) {
                this.dataService.GetEventsTAndC(eventIds)
                    .then((data: TermsAndConditionsModel[]) => {
                        this.eventTerms(data);
                        this.ShowEventTermsModal();
                    });
            }
            else {
                this.ShowEventTermsModal();
            }
        }
    };

    private goBack() {
        window.history.back();
    };

    public GetShoppingCartSuccessCallback(data: CartModel) {
        if ((data.tickets === null || data.tickets.length === 0)
            && (data.giftCertificates === null || data.giftCertificates.length === 0)) {
            window.location.href = this.bookingSitePath() + '/book/search';
            return;
        }

        const eventIds = $cartHelper.GetEventIds(data);
        this.sendCartTrackingDetails(eventIds);
        this.shoppingCartVm(data);
        this.isCartLoaded(true);

        // workaround fix for paypal confirmation page not updating frame height
        // https://github.com/davidjbradshaw/iframe-resizer/blob/master/docs/iframed_page/methods.md
        setTimeout(() => {
            if ('parentIFrame' in window) {
                (window as any).parentIFrame.autoResize(false);
                (window as any).parentIFrame.size();
                setTimeout(() => {
                    (window as any).parentIFrame.autoResize(true);
                }, 200);
            }
        }, 500);
    }

    private sendCartTrackingDetails(forEvents: number[]) {
        if (!forEvents?.length) return;
        this.dataService.GetTrackingCodes(forEvents)
            .then(result => {
                if (!result) return;

                const gViewCheckout = getEventTrackingCodesByAction(result.googleAnalyticsEventTrackingList, 'View Checkout');
                const gSubmitPayment = getEventTrackingCodesByAction(result.googleAnalyticsEventTrackingList, 'Submit Payment');
                if (gViewCheckout.length <= 0 || gSubmitPayment.length <= 0) {
                    result.googleAnalyticsEventTrackingList = [];
                }

                const {
                    facebookPixelEventTrackingList,
                    googleAnalyticsEventTrackingList,
                    tikTokPixelEventTrackingList
                } = result;


                intialiseTracking(result);
                // Facebook vm init
                const fbViewCheckout = getEventTrackingCodesByAction(facebookPixelEventTrackingList, 'View Checkout');
                const fbSubmitPayment = getEventTrackingCodesByAction(facebookPixelEventTrackingList, 'Submit Payment');
                const shouldFBTrackCheckoutView = fbViewCheckout.length > 0;
                this.shouldFBTrackSubmitPayment(fbSubmitPayment.length > 0);
                if (shouldFBTrackCheckoutView) { 
                    waitForFbq(() => fbq('track', 'AddToCart'));
                }

                // TikTok vm init
                const ttViewCheckout = getEventTrackingCodesByAction(tikTokPixelEventTrackingList, 'View Checkout');
                const ttSubmitPayment = getEventTrackingCodesByAction(tikTokPixelEventTrackingList, 'Submit Payment');
                const shouldTikTokTrackCheckoutView = ttViewCheckout.length > 0;
                this.shouldTikTokTrackSubmitPayment(ttSubmitPayment.length > 0);
                if (shouldTikTokTrackCheckoutView) {
                    waitForTtq(() => ttq.track('AddToCart'));
                }

                // Google Analytics vm init
                this.googleTrackingCodes(googleAnalyticsEventTrackingList);
                this.shouldGATrackSubmitPayment(gSubmitPayment.length > 0);
            });
    }

    public GetShoppingCartErrorCallback() {
        window.location.href = this.bookingSitePath() + '/book/search';
    }

    public ShowEventTermsModal() {
        let eventTandCDetails = '';
        const eventTerms = this.eventTerms();
        // TODO: update casing of Event object and its properties when legacy api is fully decommissioned
        $.each(eventTerms, (index: number, value: TermsAndConditionsModel) => {
            eventTandCDetails += '<h5>' + value.eventName + '</h5><p>' + value.trybookingTC + '</p>';

            eventTandCDetails += '<h5>Event organiser terms and conditions</h5><p>' + value.eventOrganiserTC + '</p>';
        });

        $bootstrapHelper.ShowModal('Terms and Conditions', eventTandCDetails);
    }

    public SubmitConfirmation() {
        if (!this.acknowledgedTerms()) {
            this.hasTermsError(true);
            this.termsErrorMessage('Agreeing to the terms and conditions is a precondition of using ' + this.uiStrings().systemName);
        }
        else if (this.ValidateFormFields()) {
            this.isLoading(true);
            sessionStorage.clear();
            let userDetails: UserBookingDetailsModel = {
              firstName: this.payerFirstName(),
              lastName: this.payerLastName(),
              emailAddress: this.bookingEmail(),
              payerEmail: this.payerEmail(),
              phoneNumber: this.payerPhoneNumber(),
              address: this.payerAddressLine1(),
              state: this.payerAddressLine2(),
              postCode: this.payerAddressPostalCode(),
              countryCode: this.payerAddressCountryCode(),
              isQuickSale: false,                
              browserTimezone: this.dataService.GetBrowserTimezone()
            };

            // fb submit payment tracking
            if (this.shouldFBTrackSubmitPayment() && window.fbq && this.shoppingCartVm().totalAmount > 0) {
                window.fbq('track', 'AddPaymentInfo');
            }

            if (this.shouldTikTokTrackSubmitPayment() && window.ttq && this.shoppingCartVm().totalAmount > 0) {
                window.ttq.track('AddPaymentInfo');
            }

            //ga submit payment tracking
            if (this.shouldGATrackSubmitPayment() && window.ga) {
                this.googleTrackingCodes().forEach(code => {
                    const totalEventAmount = $cartHelper.GetEventTotalAmount(this.shoppingCartVm(), code.eventId);
                    const gtagEvents = getEventTrackingCodesByAction(this.googleTrackingCodes(), 'Submit Payment');
                    if (window.gtag && totalEventAmount > 0) {
                        gtagEvents.forEach(g => {
                            window.gtag('config', g.trackingId, {
                                'page_path': '/book/paymentInfo'
                            });
                        });
                    }
                });
            }

            
            let callProcessPayment = function(token: string) {
                const payload: CheckoutPayload = {
                    paymentTypeName: 'PayPal',
                    nonce: window.payPalConfirmationViewModel.paypalNonce(),
                    orderId: window.payPalConfirmationViewModel.paypalOrderId(),
                    hasPermissionToContact: window.payPalConfirmationViewModel.permissionToContact(),
                    trackingTag: window.payPalConfirmationViewModel.trackingTag(),
                    customId: window.payPalConfirmationViewModel.customId(),
                    paymentGatewayId: window.payPalConfirmationViewModel.paymentGatewayId(),
                    recaptchaToken: token
                };

                window.payPalConfirmationViewModel.dataService.ProcessBooking(window.payPalConfirmationViewModel.shoppingCartVm(), userDetails, payload)
                .done((result: any) => window.payPalConfirmationViewModel.OnProcessBookingSuccess(result))
                .fail((e: any) => window.payPalConfirmationViewModel.OnProcessBookingError(e));
            }

            if (grecaptcha) {
                grecaptcha.ready(() => {
                    grecaptcha.execute((window as any).global.config.googleReCaptchaSiteKey, { action: 'submit' }).then((token) => {
                        callProcessPayment(token);
                    });
                });
            } else {
                callProcessPayment('');
            }
        } else {
            this.errorMessage('One or more fields have invalid values. Please check and try again.');
            this.showErrorMessage(true);
            $(window).scrollTop($("#checkout-error-message").offset().top - 20);
        }
    };

    public OnProcessBookingSuccess(result: any) {
        if (result) {
            if (result.errorMessage) {
                this.showErrorMessage(true);
                this.errorMessage(result.errorMessage);
                this.isLoading(false);
                if (result.resultCode === 2) {
                    if (this.shoppingCartVm().tickets) {
                        window.location.href = this._env.eventSitePath + `/${this.shoppingCartVm().tickets[0].event.id}/sessions`;
                    }
                }
            }
            else {
                this.dataService.ClearShoppingCart()
                    .done(() => this.ClearShoppingCartSuccessCallback())
                    .fail((e: any) => this.ClearShoppingCartErrorCallback(e))
                    .always(() => {
                        const widgetEventId = this.widgetEventId() ? parseInt((this.widgetEventId() as any)) : 0; // TO DO: be explicit
                        const isEventListingWidget = this.isEventListingWidget() === true || (this.isEventListingWidget() as any) === 'true';
                        const accountId = this.widgetAccountId() ? parseInt((this.widgetAccountId() as any)) : 0; // TO DO: be explicit

                        var confirmationPageUrl = this.urlService().GetConfirmationPageUrl(result.bookingUrlId, this.widgetType(), widgetEventId, isEventListingWidget, accountId, this.checkout.isWhiteLabel);

                        window.location.href = confirmationPageUrl;
                    });
            }
        }
    }

    private ClearShoppingCartSuccessCallback() {
        console.log('Shopping Cart Session Cleared');
    }

    private ClearShoppingCartErrorCallback(e: any) {
        console.error(e);
    }

    private OnProcessBookingError(error: any) {
        window.payPalConfirmationViewModel.dataService.LogJavascriptWarning({
            message: '[PayPal Confirmation] ' + error.responseJSON.errorMessage,
            name: '[PayPal Confirmation] ' + error.responseJSON.errorMessage,
            stack: new Error().stack
        } as Error);
        if (error.responseJSON.errorMessage) {
            window.payPalConfirmationViewModel.showErrorMessage(true);
            window.payPalConfirmationViewModel.errorMessage(error.responseJSON.errorMessage);
            window.payPalConfirmationViewModel.isLoading(false);
            //specific result code for SaveBookingResultCode.ReservationFailure
            if (error.resultCode === 2) {
                if (window.payPalConfirmationViewModel.shoppingCartVm().tickets) {
                    window.location.href = window.payPalConfirmationViewModel._env.eventSitePath + `/${window.payPalConfirmationViewModel.shoppingCartVm().tickets[0].event.id}/sessions`;
                }
            }
        } else {
            const errorUrl = window.payPalConfirmationViewModel.urlService().GetCheckoutErrorUrl(window.payPalConfirmationViewModel.checkout.isWhiteLabel, this.isWidget());
            window.location.href = errorUrl;
            console.error(error);
        }
    }

    private InitializeFormFields() {
        this.payerPhoneNumber = ko.observable('').extend({
            required: {
                params: true,
                message: "Phone number is required."
            },
            phonePattern: true
        });
    }

    private ValidateFormFields() {
        this.payerPhoneNumber.isModified(true);
        return this.payerPhoneNumber.isValid();
    }

    private InitRecaptcha() {
        var gRecaptchaUrl = `https://www.google.com/recaptcha/api.js?render=${(window as any).global.config.googleReCaptchaSiteKey}&size=invisible`;
        if ($('head script[src="' + gRecaptchaUrl + '"]').length == 0) {
            var head = document.getElementsByTagName('head')[0];
            var script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = gRecaptchaUrl;
            script.async = true;
            script.defer = true;
            head.appendChild(script);
        }
    }
}

